import { ColumnDef, flexRender } from "@tanstack/react-table";
import { Table as ReactTableType } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import Spinner from "@/components/Spinner";
import { useNavigate } from "react-router-dom";

interface ErrorResponse {
  success: false;
  error: {
    message: string;
    statusCode: number;
  };
}

interface DataTableProps<TData, TValue> {
  table: ReactTableType<TData>;
  columns: ColumnDef<TData, TValue>[];
  loading: boolean;
  error?: ErrorResponse | null;
}

export function DataTable<TData, TValue>({
  columns,
  loading,
  error,
  table,
}: DataTableProps<CollectionOrder, TValue>) {
  const navigate = useNavigate();
  // Loading or error states
  if (loading)
    return (
      <div className="h-[67vh] flex items-center justify-center">
        <Spinner />
      </div>
    );
  if (error)
    return (
      <div className="h-[67vh] flex items-center justify-center">
        {error.error.message}
      </div>
    );
  const handleCellClick = (cell: any, row: any) => {
    // Check if the cell is not in the actions column (replace 'actions' with the actual column ID if different)
    if (cell.column.id !== "actions") {
      // Navigate to the client detail page (you can pass the client ID or any identifier from the row data)
      navigate(`/collection/collectionorders/${row.original.id}`); // Assuming row.original contains the client data
    }
  };

  return (
    <div className="flex flex-col min-h-[77vh]">
      <div className=" rounded-[10px] border-[#ECEEF6] bg-white">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className="" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      onClick={() => handleCellClick(cell, row)}
                      className=" font-normal cursor-pointer"
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
